import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useCallback, useState } from 'react';
import { Category, CreateMovieRequest, MediaLink, Language, MovieGenre, MovieClassification } from '@/types/model';
import {
  movieGenreOptions,
  movieClassificationOptions, movieClassifications
} from '@/constants';
import { useTranslation, useCategoriesForDropdown } from '@/hooks';
import { useCreateWatermark, useWatermarks } from '@/hooks/useWatermarks';
import { Translation } from '@/locales';
import { ContentDetailsSource, contentDetailsService } from '@/services/contentDetailsService';
import { movieService } from '@/services/movieService';
import { PlaybackLanguageParams } from '@/services/tvShowService';
import VodPlayer from '@/components/media/VodPlayer';
import Input from '@/components/ui/Input';
import Select from '@/components/ui/Select';
import Toggle from '@/components/ui/Toggle';
import Button from '@/components/ui/Button';
import MultiSelect from '@/components/ui/MultiSelect';
import LinkManager from '@/components/ui/LinkManager';
import MediaAssetsSection from '@/components/ui/MediaAssetsSection';
import ImageUrlUploadField from '@/components/ui/ImageUrlUploadField';
import AutocompleteSelect from '@/components/ui/AutocompleteSelect';
import Modal from '@/components/ui/Modal';
import { RefreshCw, Search } from 'lucide-react';
import { qualityTypes } from '@/constants';
import ImageNotAvailable from '@/assets/image-not-available.png';
import { isDigitalOceanSpacesUrl } from '@/utils/validation.ts';
import { MediaSource } from '@/types/media';
import { mapExternalMovieGenres } from '@/utils/contentGenreMapping';
import TmdbContentAutocomplete from '@/components/forms/TmdbContentAutocomplete';

const createMovieSchema = (t: Translation) => z.object({
  title: z
    .string()
    .min(2, t.validation.minLength(2))
    .max(200, t.validation.maxLength(200)),
  spanishTitle: z.string().optional(),
  originalTitle: z.string().optional(),
  imdbId: z.string().nullable().transform((val) => val || null),
  tmdbId: z.string().nullable().transform((val) => val || null),
  year: z
    .number({ message: t.validation.required })
    .min(1900, 'El año debe ser mayor a 1900')
    .max(new Date().getFullYear() + 5, 'El año no puede ser mayor al año actual + 5'),
  releaseDate: z.string().optional(),
  durationMinutes: z
    .number({ message: t.validation.required })
    .min(1, 'La duración debe ser mayor a 1 minuto'),
  rating: z.number({ message: t.validation.required }).min(0, "El rating debe ser mayor que 0").max(10, "El rating no puede ser mayor que 10"),
  actors: z.string().optional(),
  spanishSynopsis: z.string().optional(),
  englishSynopsis: z.string().optional(),
  classification: z.enum(movieClassifications, t.validation.required),
  genres: z.array(z.string()).optional(),
  categoryIds: z.array(z.string()).optional(),
  coverImageUrl: z.url().optional().or(z.literal('')),
  backgroundImageUrl: z.url().optional().or(z.literal('')),
  trailerUrl: z
    .string()
    .optional()
    .or(z.literal(''))
    .refine((value) => !value || isDigitalOceanSpacesUrl(value), {
      message: 'Por favor suba el trailer a Digital Ocean',
    }),
  reference: z.string().optional(),
  watermarkId: z.string().optional(),
  active: z.boolean(),
  adultContent: z.boolean(),
  isFeatured: z.boolean(),
  kidsContent: z.boolean(),
  links: z.array(
    z.object({
      id: z.string().optional(),
      quality: z.enum(qualityTypes),
      url: z.url(t.validation.urlFormat),
      audioLanguages: z.array(z.string()).optional(),
      subtitleLanguages: z.array(z.string()).optional(),
      active: z.boolean(),
    }), t.links.required
  ).min(1, t.links.required)
});

type MovieFormData = z.infer<ReturnType<typeof createMovieSchema>>;

interface WatermarkModalFormState {
  name: string;
  imageUrl: string;
}

interface WatermarkOption {
  value: string;
  label: string;
  imageUrl: string;
}

interface MovieFormProps {
  onSuccess?: (data: CreateMovieRequest) => void;
  onCancel?: () => void;
  initialData?: Partial<CreateMovieRequest>;
  isEdit?: boolean;
  isSubmitting?: boolean;
}

const MovieForm = ({
                     onSuccess,
                     onCancel,
                     initialData,
                     isEdit = false,
                     isSubmitting = false
                   }: MovieFormProps) => {
  const { t } = useTranslation();
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [imdbSearchLoading, setImdbSearchLoading] = useState(false);
  const [imdbSearchError, setImdbSearchError] = useState<string | null>(null);
  const [detailsSource, setDetailsSource] = useState<ContentDetailsSource>(
    initialData?.tmdbId ? 'TMDB' : initialData?.imdbId ? 'IMDB' : 'TMDB',
  );
  const [showImdbOnly, setShowImdbOnly] = useState(!isEdit && !initialData?.title);
  const [selectedGenres, setSelectedGenres] = useState<string[]>(initialData?.genres || []);
  const [selectedCategories, setSelectedCategories] = useState<string[]>(initialData?.categoryIds ||  []);
  const [releaseDateLoading, setReleaseDateLoading] = useState(false);
  const [isWatermarkModalOpen, setIsWatermarkModalOpen] = useState(false);
  const [watermarkModalError, setWatermarkModalError] = useState<string | null>(null);
  const [watermarkForm, setWatermarkForm] = useState<WatermarkModalFormState>({ name: '', imageUrl: '' });
  const [playingLink, setPlayingLink] = useState<MovieFormData['links'][number] | null>(null);
  // Fetch categories for dropdown (must be before any conditional returns)
  const { data: categoriesData } = useCategoriesForDropdown('MOVIE');
  const { data: watermarks = [], isLoading: watermarksLoading } = useWatermarks();
  const createWatermarkMutation = useCreateWatermark();

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    setValue,
    reset
  } = useForm<MovieFormData>({
    resolver: zodResolver(createMovieSchema(t)),
    defaultValues: {
      title: initialData?.title || '',
      spanishTitle: initialData?.spanishTitle || '',
      originalTitle: initialData?.originalTitle || '',
      imdbId: initialData?.imdbId || null,
      tmdbId: initialData?.tmdbId || null,
      year: initialData?.year || new Date().getFullYear(),
      releaseDate: initialData?.releaseDate || '',
      durationMinutes: initialData?.durationMinutes || 0,
      rating: initialData?.rating || 0,
      actors: initialData?.actors || '',
      spanishSynopsis: initialData?.spanishSynopsis || '',
      englishSynopsis: initialData?.englishSynopsis || '',
      classification: initialData?.classification,
      genres: initialData?.genres || [],
      categoryIds: initialData?.categoryIds || [],
      coverImageUrl: initialData?.coverImageUrl || '',
      backgroundImageUrl: initialData?.backgroundImageUrl || '',
      trailerUrl: initialData?.trailerUrl || '',
      reference: initialData?.reference || '',
      watermarkId: initialData?.watermarkId || '',
      active: initialData?.active ?? true,
      adultContent: initialData?.adultContent ?? false,
      isFeatured: initialData?.isFeatured ?? false,
      kidsContent: initialData?.kidsContent ?? false,
      links: initialData?.links || []
    }
  });

  const searchDetails = async (tmdbIdOverride?: string) => {
    const imdbId = watch('imdbId') || '';
    const tmdbId = tmdbIdOverride || watch('tmdbId') || '';
    const lookupId = detailsSource === 'IMDB' ? imdbId : tmdbId;
    if (!lookupId.trim()) return;

    setImdbSearchLoading(true);
    setImdbSearchError(null);

    try {
      const movieData = await contentDetailsService.getMovie({
        source: detailsSource,
        imdbId,
        tmdbId,
      });

      setValue('title', movieData.title);
      if (movieData.imdbId) setValue('imdbId', movieData.imdbId);
      if (movieData.tmdbId) setValue('tmdbId', movieData.tmdbId);
      if (movieData.year) setValue('year', movieData.year);
      if (movieData.releaseDate) setValue('releaseDate', movieData.releaseDate);
      if (movieData.durationMinutes) setValue('durationMinutes', movieData.durationMinutes);
      setValue('actors', movieData.actors || '');
      setValue('englishSynopsis', movieData.englishSynopsis || '');
      if (movieData.spanishSynopsis) setValue('spanishSynopsis', movieData.spanishSynopsis);
      setValue('coverImageUrl', movieData.coverImageUrl || '');
      if (movieData.backgroundImageUrl) setValue('backgroundImageUrl', movieData.backgroundImageUrl);
      setValue('rating', movieData.rating ?? 0);
      if (movieData.spanishTitle) setValue('spanishTitle', movieData.spanishTitle);
      if (movieData.originalTitle) setValue('originalTitle', movieData.originalTitle);
      if (movieData.classification) setValue('classification', movieData.classification as MovieClassification);

      if (movieData.genres && movieData.genres.length > 0) {
        const { genres: mappedGenres } = mapExternalMovieGenres(movieData.genres, detailsSource);
        setSelectedGenres(mappedGenres);
        setValue('genres', mappedGenres);
      }

      setShowImdbOnly(false);
    } catch (error) {
      console.error('Content details search error:', error);
      setImdbSearchError(error instanceof Error ? error.message : `Error al buscar en ${detailsSource}`);
    } finally {
      setImdbSearchLoading(false);
      setReleaseDateLoading(false);
    }
  };

  const handleImdbSearch = () => {
    void searchDetails();
  };

  const handleTmdbSelect = (tmdbId: string) => {
    setValue('tmdbId', tmdbId || null, { shouldDirty: true });
    if (tmdbId) {
      void searchDetails(tmdbId);
    }
  };

  const onSubmit = async (data: MovieFormData) => {
    setSubmitError(null);

    try {
      const formattedData: CreateMovieRequest = {
        ...data,
        classification: data.classification as MovieClassification,
        genres: data.genres as MovieGenre[],
        links: data.links?.map(link => ({
          ...link,
          id: link.id || crypto.randomUUID(),
          audioLanguages: link.audioLanguages as Language[],
          subtitleLanguages: link.subtitleLanguages as Language[],
          createdDate: new Date().toISOString(),
          updatedDate: new Date().toISOString()
        }))
      };
      await onSuccess?.(formattedData);
      if (!isEdit) {
        reset();
        setShowImdbOnly(true);
        setSelectedGenres([]);
      }
    } catch (error) {
      setSubmitError(error instanceof Error ? error.message : t.errors.unexpectedError);
    }
  };

  const mapCategoriesToOptions = (data: unknown[]) =>
    (data as Category[]).map(cat => ({ value: cat.id, label: cat.description }));

  const imdbIdValue = watch('imdbId') || '';
  const tmdbIdValue = watch('tmdbId') || '';
  const titleValue = watch('title') || '';
  const yearValue = watch('year');
  const activeLookupId = detailsSource === 'IMDB' ? imdbIdValue : tmdbIdValue;
  const canSearchImdb = activeLookupId.trim().length > 0;
  const activeValue = watch('active');
  const adultContentValue = watch('adultContent');
  const isFeaturedValue = watch('isFeatured');
  const kidsContentValue = watch('kidsContent');
  const coverImageValue = watch('coverImageUrl');
  const backgroundImageValue = watch('backgroundImageUrl');
  const trailerUrlValue = watch('trailerUrl');
  const watermarkIdValue = watch('watermarkId');
  const watchedLinks = watch('links') || [];

  const loadPlayingLinkUrl = useCallback((params: PlaybackLanguageParams) => {
    if (!playingLink) {
      return Promise.reject(new Error('No movie link selected'));
    }
    return movieService.getLinkPlaybackUrl(playingLink, params);
  }, [playingLink]);

  const watermarkOptions: WatermarkOption[] = watermarks.map((watermark) => ({
    value: watermark.id,
    label: watermark.name,
    imageUrl: watermark.imageUrl,
  }));

  const fillManually = () => {
    setShowImdbOnly(false);
    setImdbSearchError(null);
  };

  if (showImdbOnly) {
    return (
      <div className="bg-white rounded-lg shadow-lg p-6">
        <div className="mb-6">
          <h2 className="text-xl font-semibold text-gray-900">
            Crear Nueva Película
          </h2>
          <p className="text-gray-600 mt-1">
            Seleccione una fuente y busque el contenido para completar automáticamente los campos
          </p>
        </div>

        {imdbSearchError && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-md">
            <p className="text-sm text-red-600">{imdbSearchError}</p>
          </div>
        )}

        <div className="space-y-4">
          <Select
            label="Fuente"
            options={[
              { value: 'TMDB', label: 'TMDB' },
              { value: 'IMDB', label: 'IMDB' },
            ]}
            value={detailsSource}
            onChange={(event) => setDetailsSource(event.target.value as ContentDetailsSource)}
          />
          {detailsSource === 'IMDB' ? (
            <div className="flex items-end gap-4">
              <div className="flex-1">
                <Input
                  label="ID de IMDB"
                  placeholder="tt1234567 o 1234567"
                  helperText="Ingrese el ID de IMDB y presione Enter o haga clic en buscar"
                  {...register('imdbId')}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleImdbSearch();
                    }
                  }}
                />
              </div>
              <Button
                type="button"
                onClick={handleImdbSearch}
                loading={imdbSearchLoading}
                disabled={imdbSearchLoading || !canSearchImdb}
              >
                <span className="inline-flex items-center gap-2" translate="no">
                  <Search className="w-4 h-4" />
                  <span>Buscar</span>
                </span>
              </Button>
            </div>
          ) : (
            <TmdbContentAutocomplete
              kind="movie"
              value={tmdbIdValue}
              selectedTitle={titleValue}
              selectedYear={yearValue}
              loading={imdbSearchLoading}
              onChange={handleTmdbSelect}
            />
          )}

          <div className="pt-4 border-t border-gray-200">
            <Button
              type="button"
              variant="outline"
              onClick={fillManually}
              className="w-full"
            >
              Llenar formulario manualmente
            </Button>
          </div>
        </div>
      </div>
    );
  }

  const handleAddLink = async (link: Omit<MediaLink, 'id' | 'createdDate' | 'updatedDate'>) => {
    try {
      const newLink = {
        ...link,
        id: crypto.randomUUID(),
        audioLanguages: link.audioLanguages?.map(a => a.toUpperCase()) || [],
        subtitleLanguages: link.subtitleLanguages?.map(s => s.toUpperCase()) || [],
      };
      setValue('links', [...watchedLinks, newLink]);
    } catch (error) {
      console.error('Error adding link:', error);
    }
  };

  const handleDeleteLink = async (linkId: string) => {
    try {
      setValue('links', watchedLinks.filter(link => link.id !== linkId));
    } catch (error) {
      console.error('Error deleting link:', error);
    }
  };

  const handleUpdateLink = async (linkId: string, updatedLink: Omit<MediaLink, 'id' | 'createdDate' | 'updatedDate'>) => {
    try {
      const updatedLinks = watchedLinks.map(link =>
        link.id === linkId
          ? {
              ...link,
              ...updatedLink,
              audioLanguages: updatedLink.audioLanguages?.map(a => a.toUpperCase()) || [],
              subtitleLanguages: updatedLink.subtitleLanguages?.map(s => s.toUpperCase()) || [],
            }
          : link
      );
      setValue('links', updatedLinks);
    } catch (error) {
      console.error('Error updating link:', error);
    }
  };

  const handleToggleLink = async (linkId: string, active: boolean) => {
    try {
      const updatedLinks = watchedLinks.map(link =>
        link.id === linkId ? { ...link, active } : link
      );
      setValue('links', updatedLinks);
    } catch (error) {
      console.error('Error toggling link:', error);
    }
  };

  const handleBackgroundImageChange = (url: string) => {
    setValue('backgroundImageUrl', url);
  };

  const handleTrailerUrlChange = (url: string) => {
    setValue('trailerUrl', url);
  };

  const openWatermarkModal = () => {
    setWatermarkForm({ name: '', imageUrl: '' });
    setWatermarkModalError(null);
    setIsWatermarkModalOpen(true);
  };

  const closeWatermarkModal = () => {
    if (createWatermarkMutation.isPending) {
      return;
    }
    setIsWatermarkModalOpen(false);
    setWatermarkModalError(null);
    setWatermarkForm({ name: '', imageUrl: '' });
  };

  const handleCreateWatermark = async () => {
    setWatermarkModalError(null);

    if (!watermarkForm.name.trim()) {
      setWatermarkModalError('El nombre es requerido');
      return;
    }

    if (!watermarkForm.imageUrl.trim()) {
      setWatermarkModalError('La imagen es requerida');
      return;
    }

    try {
      const response = await createWatermarkMutation.mutateAsync({
        name: watermarkForm.name.trim(),
        imageUrl: watermarkForm.imageUrl.trim(),
      });
      setValue('watermarkId', response.watermark.id, { shouldDirty: true, shouldValidate: true });
      closeWatermarkModal();
    } catch (error) {
      setWatermarkModalError(error instanceof Error ? error.message : t.errors.unexpectedError);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-lg p-6">
      <div className="mb-6">
        <div className="flex items-center justify-between mb-2">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">
              {isEdit ? 'Editar Película' : 'Crear Nueva Película'}
            </h2>
            <p className="text-gray-600">
              {isEdit ? 'Actualice la información de la película' : 'Complete todos los campos requeridos'}
            </p>
          </div>
          {!isEdit && (
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setShowImdbOnly(true);
                reset();
                setSelectedGenres([]);
              }}
              className="flex items-center gap-2"
            >
              <Search className="w-4 h-4"/>
              Buscar detalles
            </Button>
          )}
        </div>

      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        {/* Top Section with Cover Image */}
        <div className="flex gap-6">
          {/* Left Side - Form Fields */}
          <div className="flex-1 space-y-4">
            <Input
              label="Título *"
              placeholder="Título de la película"
              error={errors.title?.message}
              {...register('title')}
            />

            <div className="space-y-1">
              <label className="block text-sm font-medium text-gray-700">
                Fuente de detalles
              </label>
              <Select
                options={[
                  { value: 'TMDB', label: 'TMDB' },
                  { value: 'IMDB', label: 'IMDB' },
                ]}
                value={detailsSource}
                onChange={(event) => setDetailsSource(event.target.value as ContentDetailsSource)}
              />
            </div>

            {detailsSource === 'IMDB' ? (
              <div className="space-y-1">
                <div className="flex rounded-md shadow-sm">
                  <input
                    placeholder="tt1234567"
                    value={imdbIdValue}
                    onChange={(event) => setValue('imdbId', event.target.value)}
                    className={`
                      flex-1 px-3 py-2 border border-gray-300 rounded-l-md rounded-r-none border-r-0
                      focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500
                      disabled:bg-gray-50 disabled:text-gray-500 disabled:cursor-not-allowed
                      ${errors.imdbId ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : ''}
                    `}
                  />
                  <Button
                    type="button"
                    variant="primary"
                    size="md"
                    className="rounded-l-none border-l-0"
                    onClick={handleImdbSearch}
                    loading={imdbSearchLoading}
                    disabled={imdbSearchLoading || !canSearchImdb}
                  >
                    Actualizar
                  </Button>
                </div>
                {errors.imdbId?.message && (
                  <p className="text-sm text-red-600">{errors.imdbId.message}</p>
                )}
              </div>
            ) : (
              <div className="flex flex-col gap-3 sm:flex-row sm:items-start">
                <div className="min-w-0 flex-1">
                  <TmdbContentAutocomplete
                    kind="movie"
                    value={tmdbIdValue}
                    selectedTitle={titleValue}
                    selectedYear={yearValue}
                    loading={imdbSearchLoading}
                    disabled={isSubmitting}
                    onChange={handleTmdbSelect}
                  />
                </div>
                <Button
                  type="button"
                  variant="primary"
                  size="md"
                  className="w-full sm:mt-6 sm:w-auto"
                  title="Actualizar detalles desde TMDB"
                  onClick={handleImdbSearch}
                  loading={imdbSearchLoading}
                  disabled={imdbSearchLoading || !canSearchImdb || isSubmitting}
                >
                  {!imdbSearchLoading && <RefreshCw className="mr-2 h-4 w-4" />}
                  Actualizar
                </Button>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-1">
                <label className="block text-sm font-medium text-gray-700">
                  ID de IMDB
                </label>
                <input
                  placeholder="tt1234567"
                  {...register('imdbId')}
                  className={`
                    w-full px-3 py-2 border border-gray-300 rounded-md
                    focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500
                    disabled:bg-gray-50 disabled:text-gray-500 disabled:cursor-not-allowed
                    ${errors.imdbId ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : ''}
                  `}
                />
                {errors.imdbId?.message && (
                  <p className="text-sm text-red-600">
                    {errors.imdbId.message}
                  </p>
                )}
              </div>
              <div className="space-y-1">
                <label className="block text-sm font-medium text-gray-700">
                  ID de TMDB
                </label>
                <input
                  placeholder="550"
                  {...register('tmdbId')}
                  className={`
                    w-full px-3 py-2 border border-gray-300 rounded-md
                    focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500
                    disabled:bg-gray-50 disabled:text-gray-500 disabled:cursor-not-allowed
                    ${errors.tmdbId ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : ''}
                  `}
                />
                {errors.tmdbId?.message && (
                  <p className="text-sm text-red-600">
                    {errors.tmdbId.message}
                  </p>
                )}
              </div>
            </div>

            <ImageUrlUploadField
              label="Imagen Cover"
              placeholder="https://example.com/cover.jpg"
              value={coverImageValue || ''}
              onChange={(url) => setValue('coverImageUrl', url, { shouldValidate: true, shouldDirty: true })}
              error={errors.coverImageUrl?.message}
              disabled={isSubmitting}
              source={MediaSource.MOVIE_POSTER}
              showPreview={false}
            />
          </div>

          {/* Right Side - Cover Image Preview */}
          <div className="flex-shrink-0">
            {coverImageValue ? (
              <img
                src={coverImageValue}
                alt="Vista previa de la portada"
                className="h-58 w-40 object-cover rounded-lg shadow-md"
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  target.src = ImageNotAvailable;
                  target.style.display = 'block';
                }}
                onLoad={(e) => {
                  const target = e.target as HTMLImageElement;
                  target.style.display = 'block';
                }}
              />
            ) : (
              <div
                className="h-58 w-40 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center bg-gray-50">
                <div className="text-center text-gray-500">
                  <div className="text-xs">Vista previa</div>
                  <div className="text-xs">de portada</div>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">

          <Input
            label="Título en español"
            placeholder="Título en español"
            error={errors.spanishTitle?.message}
            {...register('spanishTitle')}
          />

          <Input
            label="Título original"
            placeholder="Original Title"
            error={errors.originalTitle?.message}
            {...register('originalTitle')}/>

          <Input
            label="Año *"
            type="number"
            placeholder="2024"
            error={errors.year?.message}
            {...register('year', { valueAsNumber: true })}
          />

          <Input
            label="Fecha de Lanzamiento"
            type="date"
            error={errors.releaseDate?.message}
            helperText={releaseDateLoading ? 'Buscando fecha de lanzamiento en IMDB...' : undefined}
            helperLoading={releaseDateLoading}
            disabled={releaseDateLoading}
            {...register('releaseDate')}
          />

          <Input
            label="Duración (Minutos) *"
            type="number"
            placeholder="120"
            error={errors.durationMinutes?.message}
            {...register('durationMinutes', { valueAsNumber: true })}
          />

          <Input
            label="Rating"
            type="number"
            step={0.1}
            placeholder="8.5/10"
            error={errors.rating?.message}
            {...register('rating', { valueAsNumber: true })}
          />
        </div>

        {/* Actors */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Actores
          </label>
          <textarea
            placeholder="Leonardo DiCaprio, Kate Winslet, Billy Zane..."
            rows={3}
            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            {...register('actors')}
          />
        </div>

        {/* Synopsis */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Sinopsis en Español
            </label>
            <textarea
              placeholder="Sinopsis en español..."
              rows={4}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              {...register('spanishSynopsis')}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Sinopsis en Inglés
            </label>
            <textarea
              placeholder="Synopsis in English..."
              rows={4}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              {...register('englishSynopsis')}
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <Select
            label="Clasificación"
            options={movieClassificationOptions}
            placeholder="Seleccionar clasificación"
            error={errors.classification?.message}
            {...register('classification')}
          />

          <MultiSelect
            label="Categorías"
            placeholder="Selecciona categorías..."
            options={mapCategoriesToOptions(categoriesData || [])}
            value={selectedCategories}
            onChange={(newCategories) => {
              setSelectedCategories(newCategories);
              setValue('categoryIds', newCategories);
            }}
            disabled={isSubmitting}
            error={errors.categoryIds?.message}
          />
          <MultiSelect
            label="Géneros"
            placeholder="Selecciona los géneros de la película..."
            options={movieGenreOptions}
            value={selectedGenres}
            onChange={(newGenres) => {
              setSelectedGenres(newGenres);
              setValue('genres', newGenres);
            }}
            disabled={isSubmitting}
          />
        </div>

        <MediaAssetsSection
          backgroundImageUrl={backgroundImageValue || ''}
          trailerUrl={trailerUrlValue || ''}
          onBackgroundImageChange={handleBackgroundImageChange}
          onTrailerUrlChange={handleTrailerUrlChange}
          backgroundImageSource={MediaSource.MOVIE_BACKGROUND}
          backgroundImageError={errors.backgroundImageUrl?.message}
          trailerUrlError={errors.trailerUrl?.message}
        />

        <AutocompleteSelect
          label="Marca de Agua"
          placeholder="Selecciona una marca de agua"
          value={watermarkIdValue}
          options={watermarkOptions}
          onChange={(value) => setValue('watermarkId', value, { shouldDirty: true, shouldValidate: true })}
          loading={watermarksLoading}
          disabled={isSubmitting}
          emptyMessage="No hay marcas de agua disponibles"
          footerActionLabel="+ Agregar marca de agua"
          onFooterAction={openWatermarkModal}
          renderOption={(option) => {
            const watermarkOption = option as WatermarkOption;
            return (
              <div className="flex items-center gap-3">
                <img
                  src={watermarkOption.imageUrl}
                  alt={watermarkOption.label}
                  className="h-6 w-9 bg-white object-contain"
                  onError={(event) => {
                    const target = event.target as HTMLImageElement;
                    target.src = ImageNotAvailable;
                  }}
                />
                <span className="truncate text-sm font-medium text-gray-900">{watermarkOption.label}</span>
              </div>
            );
          }}
          renderSelectedOption={(option) => {
            const watermarkOption = option as WatermarkOption;
            return (
              <>
                <img
                  src={watermarkOption.imageUrl}
                  alt={watermarkOption.label}
                  className="h-6 w-9 bg-white object-contain"
                  onError={(event) => {
                    const target = event.target as HTMLImageElement;
                    target.src = ImageNotAvailable;
                  }}
                />
                <span className="truncate">{watermarkOption.label}</span>
              </>
            );
          }}
        />

        {/* Toggles */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          <Toggle
            label="Activo"
            checked={activeValue}
            onChange={(checked) => setValue('active', checked)}
            disabled={isSubmitting}
          />

          <Toggle
            label="Para Adultos"
            checked={adultContentValue}
            onChange={(checked) => setValue('adultContent', checked)}
            disabled={isSubmitting}
          />

          <Toggle
            label="Para Niños"
            checked={kidsContentValue}
            onChange={(checked) => setValue('kidsContent', checked)}
            disabled={isSubmitting}
          />

          <Toggle
            label="Destacada"
            checked={isFeaturedValue}
            onChange={(checked) => setValue('isFeatured', checked)}
            disabled={isSubmitting}
          />
        </div>

        <LinkManager
          links={watchedLinks}
          onAddLink={handleAddLink}
          onUpdateLink={handleUpdateLink}
          onDeleteLink={handleDeleteLink}
          onToggleLink={handleToggleLink}
          onPlayLink={setPlayingLink}
          error={errors.links?.message}
        />

        {submitError && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-md">
            <p className="text-sm text-red-600">{submitError}</p>
          </div>
        )}

        <div className="flex justify-end space-x-4 pt-6 border-t border-gray-200">
          {onCancel && (
            <Button
              type="button"
              variant="outline"
              onClick={onCancel}
              disabled={isSubmitting}
            >
              {t.buttons.cancel}
            </Button>
          )}
          <Button
            type="submit"
            loading={isSubmitting}
            disabled={isSubmitting}
          >
            {isSubmitting
              ? 'Guardando...'
              : isEdit
                ? 'Actualizar Película'
                : 'Crear Película'
            }
          </Button>
        </div>
      </form>

      <Modal
        isOpen={Boolean(playingLink)}
        onClose={() => setPlayingLink(null)}
        title={playingLink
          ? `${t.vodPlayer.movieTitle}: ${titleValue} — ${playingLink.quality}`
          : t.vodPlayer.movieTitle}
        size="2xl"
      >
        {playingLink && (
          <VodPlayer
            poster={coverImageValue}
            title={titleValue}
            loadPlaybackUrl={loadPlayingLinkUrl}
          />
        )}
      </Modal>

      <Modal
        isOpen={isWatermarkModalOpen}
        onClose={closeWatermarkModal}
        title="Nueva Marca de Agua"
        size="lg"
        closeOnBackdrop={!createWatermarkMutation.isPending}
        closeOnEsc={!createWatermarkMutation.isPending}
      >
        <div className="space-y-4">
          <Input
            label="Nombre"
            placeholder="Nombre de la marca de agua"
            value={watermarkForm.name}
            onChange={(event) => setWatermarkForm((current) => ({ ...current, name: event.target.value }))}
            disabled={createWatermarkMutation.isPending}
          />

          <ImageUrlUploadField
            label="Imagen"
            placeholder="https://example.com/watermark.png"
            value={watermarkForm.imageUrl}
            onChange={(url) => setWatermarkForm((current) => ({ ...current, imageUrl: url }))}
            disabled={createWatermarkMutation.isPending}
            source={MediaSource.WATERMARK_IMAGE}
            showPreview
            previewClassName="h-32 w-full object-contain rounded-lg border border-gray-200 bg-white p-2"
            previewContainerClassName="h-32 w-full border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center bg-gray-50"
          />

          {watermarkModalError && (
            <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-600">
              {watermarkModalError}
            </div>
          )}

          <div className="flex justify-end gap-3 border-t border-gray-200 pt-4">
            <Button type="button" variant="outline" onClick={closeWatermarkModal} disabled={createWatermarkMutation.isPending}>
              Cancelar
            </Button>
            <Button type="button" onClick={() => void handleCreateWatermark()} loading={createWatermarkMutation.isPending} disabled={createWatermarkMutation.isPending}>
              Guardar
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default MovieForm;
